import React from 'react';
import {
  Box, Chip, CircularProgress, Divider, Drawer, IconButton,
  Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  Tooltip, Typography, Button,
} from '@mui/material';
import { Close, Print, SwapHoriz } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useGetPosExchangeByIdQuery } from '../../services/exchangeApi';
import { useToast } from '../common/ToastProvider';
import { usePrinterContext, browserPrintExchange } from '../../context/PrinterContext';
import { mapExchangeToReceipt } from './receipt/mapExchangeToReceipt';
import { useGetReceiptTemplateQuery } from '../../services/receiptTemplateApi';
import ItemBarcode from './ItemBarcode';

const fmt = (v) => `₹${parseFloat(v || 0).toFixed(2)}`;

const STATUS_CFG = {
  completed: { label: 'Completed', bgcolor: '#ECFDF5', color: '#059669', dot: '#10B981' },
  draft:     { label: 'Draft',     bgcolor: '#FFFBEB', color: '#B45309', dot: '#F59E0B' },
  cancelled: { label: 'Cancelled', bgcolor: '#FEF2F2', color: '#DC2626', dot: '#EF4444' },
};

function ExchangeStatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || { label: status, bgcolor: '#F1F5F9', color: '#64748B', dot: '#94A3B8' };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.35, borderRadius: '6px', bgcolor: cfg.bgcolor }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: cfg.dot, flexShrink: 0 }} />
      <Typography fontSize="0.7rem" fontWeight={700} color={cfg.color} lineHeight={1}>{cfg.label}</Typography>
    </Box>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography fontSize="0.66rem" fontWeight={800} color="#94A3B8" letterSpacing="0.06rem" sx={{ mb: 0.75, px: 0.5 }}>
      {children}
    </Typography>
  );
}

function ItemsTable({ items, amountLabel = 'Amount' }) {
  if (!items || items.length === 0) return (
    <Typography fontSize="0.78rem" color="#94A3B8" fontStyle="italic" sx={{ py: 1 }}>—</Typography>
  );
  return (
    <Box sx={{ overflow: 'auto' }}>
      <Table size="small" sx={{ minWidth: 280 }}>
        <TableHead>
          <TableRow>
            {['Product', 'Qty', amountLabel].map((h) => (
              <TableCell key={h} sx={{ fontWeight: 800, fontSize: '0.65rem', color: '#94A3B8', pb: 0.5, pt: 0, textTransform: 'uppercase', letterSpacing: '0.04rem' }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, i) => (
            <TableRow key={item.id || i}>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem', color: '#1E293B', fontWeight: 600 }}>
                <div>{item.product_name}</div>
                <ItemBarcode code={item.display_barcode} fontSize="0.68rem" />
                {item.reason_name && (
                  <Typography fontSize="0.68rem" color="#94A3B8">{item.reason_name}</Typography>
                )}
              </TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem', color: '#374151' }}>
                {item.quantity || item.quantity_returned}
              </TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem', fontWeight: 700, color: '#1E293B' }}>
                {fmt(item.refund_amount || item.line_total)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function SettlementBox({ exchange }) {
  const dir = exchange.settlement_direction;
  const bgColors = { customer_pays: '#dbeafe', customer_refund: '#f3e8ff', even: '#dcfce7' };
  const textColors = { customer_pays: '#1d4ed8', customer_refund: '#7c3aed', even: '#15803d' };
  const bg = bgColors[dir] || '#F1F5F9';
  const tc = textColors[dir] || '#374151';

  return (
    <Box sx={{ bgcolor: bg, borderRadius: '10px', p: 1.5 }}>
      <Stack spacing={0.5}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography fontSize="0.78rem" color="#374151">Return value</Typography>
          <Typography fontSize="0.78rem" fontWeight={600} color="#374151">{fmt(exchange.return_total)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography fontSize="0.78rem" color="#374151">New items</Typography>
          <Typography fontSize="0.78rem" fontWeight={600} color="#374151">{fmt(exchange.new_items_total)}</Typography>
        </Box>
        <Divider sx={{ my: 0.25 }} />
        {dir === 'even' ? (
          <Typography fontSize="0.82rem" fontWeight={800} color={tc} textAlign="center">
            Even exchange — no payment
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography fontSize="0.82rem" fontWeight={800} color={tc}>
              {dir === 'customer_pays' ? 'Customer paid' : 'Refund issued'}
            </Typography>
            <Typography fontSize="0.82rem" fontWeight={800} color={tc}>
              {fmt(exchange.net_settlement_amount)}
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

export default function ExchangeDetailDrawer({ open, onClose, orderId, exchangeId }) {
  const navigate = useNavigate();
  const showToast = useToast();
  const { isConnected, silentPrintExchange } = usePrinterContext();
  const { data: receiptTemplate } = useGetReceiptTemplateQuery();
  const { data, isFetching } = useGetPosExchangeByIdQuery(
    { orderId, id: exchangeId },
    { skip: !open || !orderId || !exchangeId },
  );

  const exchange = data?.data?.exchange || data?.exchange || null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 500 },
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#F8FAFC',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, py: 2, bgcolor: '#fff', borderBottom: '1px solid #E2E8F0', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#EDE9FE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <SwapHoriz sx={{ color: '#7C3AED', fontSize: 18 }} />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography fontWeight={800} fontSize="0.92rem" color="#1E293B" sx={{ lineHeight: 1.25 }}>
              Exchange Details
            </Typography>
            {exchange && (
              <Typography fontSize="0.68rem" color="#94A3B8" sx={{ mt: 0.25 }}>
                {new Date(exchange.completed_at || exchange.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
              </Typography>
            )}
          </Box>
          {exchange && <ExchangeStatusBadge status={exchange.status} />}
          <IconButton size="small" onClick={onClose} sx={{ color: '#64748B', flexShrink: 0, mt: -0.25 }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{
        flexGrow: 1, overflowY: 'auto', p: 2,
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' },
      }}>
        {isFetching ? (
          <Stack spacing={1.5}>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} height={56} sx={{ borderRadius: '10px' }} variant="rectangular" />
            ))}
          </Stack>
        ) : !exchange ? (
          <Box sx={{ textAlign: 'center', py: 6, color: '#94A3B8' }}>
            <SwapHoriz sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
            <Typography fontSize="0.85rem" fontWeight={600}>Exchange not found</Typography>
          </Box>
        ) : (
          <Stack spacing={2}>

            {/* Draft / Cancelled banners */}
            {exchange.status === 'draft' && (
              <Box sx={{ bgcolor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', px: 2, py: 1.25 }}>
                <Typography fontSize="0.8rem" color="#92400E" fontWeight={600}>
                  Draft — not yet completed. No invoices generated.
                </Typography>
              </Box>
            )}
            {exchange.status === 'cancelled' && (
              <Box sx={{ bgcolor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', px: 2, py: 1.25 }}>
                <Typography fontSize="0.8rem" color="#B91C1C" fontWeight={600}>
                  This exchange was cancelled.
                </Typography>
              </Box>
            )}

            {/* RETURN SIDE */}
            <Box>
              <SectionLabel>RETURN SIDE</SectionLabel>
              <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E2E8F0', p: 2 }}>
                {exchange.return_side?.credit_note_number && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography fontSize="0.75rem" color="#64748B">Credit Note</Typography>
                    <Typography fontSize="0.75rem" fontWeight={700} color="#7C3AED">
                      {exchange.return_side.credit_note_number}
                    </Typography>
                  </Box>
                )}
                {exchange.return_side?.return_number && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography fontSize="0.75rem" color="#64748B">Return #</Typography>
                    <Typography fontSize="0.75rem" fontWeight={700} color="#C2410C">
                      {exchange.return_side.return_number}
                    </Typography>
                  </Box>
                )}
                {exchange.return_side?.refund_method && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography fontSize="0.75rem" color="#64748B">Refund method</Typography>
                    <Chip label={exchange.return_side.refund_method} size="small"
                      sx={{ fontSize: '0.65rem', fontWeight: 700, height: 20, bgcolor: '#F5F3FF', color: '#7C3AED' }} />
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <ItemsTable items={exchange.return_side?.items || []} amountLabel="Refund" />
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography fontSize="0.82rem" fontWeight={700} color="#374151">Return Total</Typography>
                  <Typography fontSize="0.88rem" fontWeight={800} color="#C2410C">{fmt(exchange.return_total)}</Typography>
                </Box>
              </Box>
            </Box>

            {/* NEW SALE SIDE */}
            <Box>
              <SectionLabel>NEW SALE SIDE</SectionLabel>
              <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E2E8F0', p: 2 }}>
                {exchange.new_sale_side?.order_number && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography fontSize="0.75rem" color="#64748B">Invoice</Typography>
                    <Typography fontSize="0.75rem" fontWeight={700} color="#4F46E5">
                      {exchange.new_sale_side.order_number}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <ItemsTable items={exchange.new_sale_side?.items || []} amountLabel="Amount" />
                {(exchange.new_sale_side?.payments || []).length > 0 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography fontSize="0.68rem" fontWeight={800} color="#94A3B8" letterSpacing="0.06rem" sx={{ mb: 0.75 }}>
                      PAYMENT
                    </Typography>
                    {exchange.new_sale_side.payments.map((p, i) => (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography fontSize="0.78rem" color="#374151">
                          {p.payment_mode_name || p.payment_mode}
                        </Typography>
                        <Typography fontSize="0.78rem" fontWeight={700} color="#059669">{fmt(p.amount)}</Typography>
                      </Box>
                    ))}
                  </>
                )}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography fontSize="0.82rem" fontWeight={700} color="#374151">New Sale Total</Typography>
                  <Typography fontSize="0.88rem" fontWeight={800} color="#4F46E5">{fmt(exchange.new_items_total)}</Typography>
                </Box>
              </Box>
            </Box>

            {/* SETTLEMENT */}
            <Box>
              <SectionLabel>SETTLEMENT</SectionLabel>
              <SettlementBox exchange={exchange} />
            </Box>

            {/* Parties */}
            <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E2E8F0', p: 2 }}>
              {exchange.cashier?.name && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography fontSize="0.75rem" color="#64748B">Cashier</Typography>
                  <Typography fontSize="0.75rem" fontWeight={600} color="#374151">{exchange.cashier.name}</Typography>
                </Box>
              )}
              {exchange.terminal?.name && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography fontSize="0.75rem" color="#64748B">Terminal</Typography>
                  <Typography fontSize="0.75rem" fontWeight={600} color="#374151">{exchange.terminal.name}</Typography>
                </Box>
              )}
            </Box>

          </Stack>
        )}
      </Box>

      {/* Footer */}
      {exchange && (
        <Box sx={{ px: 2.5, py: 1.75, bgcolor: '#fff', borderTop: '1px solid #E2E8F0', flexShrink: 0, display: 'flex', gap: 1 }}>
          <Tooltip title={exchange.status !== 'completed' ? 'Only completed exchanges can be printed' : ''}>
            <span style={{ flex: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Print sx={{ fontSize: 14 }} />}
                fullWidth
                disabled={exchange.status !== 'completed'}
                onClick={async () => {
                  const receipt = mapExchangeToReceipt(data, receiptTemplate);
                  if (!receipt) return;
                  if (isConnected) {
                    try { await silentPrintExchange(receipt); showToast('Exchange bill printed', 'success'); return; } catch (err) {
                      console.error('[Printer] silentPrintExchange failed:', err);
                      showToast('Printer error — switching to browser print', 'warning');
                    }
                  } else {
                    showToast('Printer app not connected — printing via browser', 'info');
                  }
                  await new Promise((r) => setTimeout(r, 1500));
                  browserPrintExchange(receipt);
                }}
                sx={{
                  textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', borderRadius: '8px',
                  color: '#4F46E5', borderColor: '#C7D2FE', '&:hover': { bgcolor: '#EEF2FF', borderColor: '#A5B4FC' },
                }}
              >
                Print
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            disableElevation
            onClick={onClose}
            sx={{
              flex: 1, textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', borderRadius: '8px',
              bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' },
            }}
          >
            Close
          </Button>
        </Box>
      )}
    </Drawer>
  );
}
